import axios from 'axios';

const defaultBackendUrl = "https://controle.imaginar.kinghost.net/api";
const defaultImageUrl = "https://imaginar.kinghost.net/imagensproducao";

export const backendUrl = (process.env.VUE_APP_API_URL || defaultBackendUrl).replace(/\/$/, '');
export const imageBaseUrl = (process.env.VUE_APP_IMG_URL || defaultImageUrl).replace(/\/$/, '');
export const apiUrl = backendUrl.endsWith('/api') ? backendUrl : `${backendUrl}/api`;

export function rawValue(source, key) {
  if (!source || !key) return '';

  const candidates = [key];

  const compactKey = String(key).replace(/_(btn|extra)(\d+)$/i, '$1$2');
  if (compactKey !== key) candidates.push(compactKey);

  const snakeBtnKey = String(key).replace(/(btn|extra)(\d+)$/i, '_$1$2');
  if (snakeBtnKey !== key) candidates.push(snakeBtnKey);

  for (const candidate of candidates) {
    if (source[candidate] !== undefined && source[candidate] !== null) return source[candidate];

    const camel = candidate.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (source[camel] !== undefined && source[camel] !== null) return source[camel];

    const pascalKey = camel.charAt(0).toUpperCase() + camel.slice(1);
    if (source[pascalKey] !== undefined && source[pascalKey] !== null) return source[pascalKey];
  }

  return '';
}

export function contentValue(source, key, fallback = '') {
  const value = rawValue(source, key);
  if (value === '' || value === null || value === undefined || value === 'NULL') return fallback;
  return value;
}

export function isVisible(item) {
  const visible = rawValue(item, 'iti_visivel') || rawValue(item, 'itg_visivel') || rawValue(item, 'igs_visivel');
  return visible === '' || visible === true || visible === 1 || visible === '1' || visible === 'true';
}

export function sortVisible(items, orderKey = 'iti_ordem') {
  return (Array.isArray(items) ? items : [])
    .filter(isVisible)
    .sort((left, right) => Number(rawValue(left, orderKey) || 0) - Number(rawValue(right, orderKey) || 0));
}

export function imagePath(value) {
  if (!value) return '';
  const text = String(value).trim();
  if (/^(https?:|data:)/i.test(text)) return text;
  return `${imageBaseUrl}/${text.replace(/^\/+/, '')}`;
}

export function firstImage(item) {
  const direct =
    rawValue(item, 'itg_urlimagem') ||
    item?.itgUrlImagem ||
    item?.itgURLImagem ||
    item?.urlImagem ||
    item?.url;
  if (direct) return imagePath(direct);

  const images = item?.insInstitucionalImagens || item?.imagens || item?.images || [];
  const image = sortVisible(images, 'itg_ordem')[0] || images[0];
  const imageUrl =
    rawValue(image, 'itg_urlimagem') ||
    image?.itgUrlImagem ||
    image?.itgURLImagem ||
    image?.urlImagem ||
    image?.url;
  return imagePath(imageUrl);
}

export function allImages(item) {
  const images = item?.insInstitucionalImagens || item?.imagens || item?.images || [];
  return sortVisible(images, 'itg_ordem')
    .map((image) => imagePath(
      rawValue(image, 'itg_urlimagem') ||
      image?.itgUrlImagem ||
      image?.itgURLImagem ||
      image?.urlImagem ||
      image?.url
    ))
    .filter(Boolean);
}

export function imagesByGallery(item, galleryName) {
  const images = item?.insInstitucionalImagens || item?.imagens || item?.images || [];
  const expected = String(galleryName || '').trim().toLocaleLowerCase('pt-BR');
  return sortVisible(images, 'itg_ordem')
    .filter((image) => {
      const gallery = image?.insListaGaleriaInstImagens || image?.listaGaleria || image?.gallery || {};
      const name = rawValue(gallery, 'lgi_nome') || gallery?.nome || gallery?.name || '';
      return String(name).trim().toLocaleLowerCase('pt-BR') === expected;
    })
    .map((image) => imagePath(
      rawValue(image, 'itg_urlimagem') ||
      image?.itgUrlImagem ||
      image?.itgURLImagem ||
      image?.urlImagem ||
      image?.url
    ))
    .filter(Boolean);
}

export function imageByGallery(item, galleryName) {
  return imagesByGallery(item, galleryName)[0] || '';
}

export function gridItems(item) {
  const list = item?.insInstitucionalGrids || item?.grids || item?.grid || [];
  return sortVisible(list, 'igs_ordem');
}

export function normalizeInternalLink(value, fallback = '') {
  if (!value) return fallback;
  const text = String(value).trim();
  if (!text) return fallback;
  if (/^(https?:|mailto:|tel:|whatsapp:|#)/i.test(text)) return text;
  const clean = text.replace(/^\.\//, '').replace(/\.html?$/i, '').replace(/^index$/i, '');
  return clean ? `/${clean.replace(/^\/+/, '')}` : '/';
}

export async function getInstitutionalContent(ctiId, multi = false) {
  const response = await axios.get(`${apiUrl}/Public/content/institucional/${ctiId}`);
  const payload = response.data?.data || response.data?.result || response.data;
  const list = Array.isArray(payload) ? payload : (payload ? [payload] : []);
  return multi ? sortVisible(list) : (list[0] || {});
}

export function plainText(value) {
  if (value === null || value === undefined) return '';
  if (typeof document === 'undefined') {
    return String(value).replace(/<[^>]*>/g, '').trim();
  }

  const element = document.createElement('div');
  element.innerHTML = String(value);
  return (element.textContent || element.innerText || '').trim();
}

function setNamedMeta(name, content) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="' + name + '"]');
  if (!content) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', plainText(content));
}

function setPropertyMeta(property, content) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[property="' + property + '"]');
  if (!content) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', plainText(content));
}

function setHttpEquivMeta(httpEquiv, content) {
  if (typeof document === 'undefined' || !content) return;
  let tag = document.querySelector('meta[http-equiv="' + httpEquiv + '"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('http-equiv', httpEquiv);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function absoluteUrl(value) {
  if (!value || typeof window === 'undefined') return plainText(value);
  try {
    return new URL(String(value), window.location.origin).href;
  } catch (_) {
    return plainText(value);
  }
}

function setCanonical(value) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', value);
}

export function applyPageMeta({
  title,
  description,
  image = '',
  type = 'website',
  siteName = 'Imaginar | Diagnóstico Veterinário',
  robots = 'index, follow, max-image-preview:large',
  canonical = '',
} = {}) {
  if (typeof document === 'undefined') return;

  const cleanTitle = plainText(title);
  const cleanDescription = plainText(description) || cleanTitle;
  const cleanSiteName = plainText(siteName);
  const pageTitle = cleanTitle && !cleanTitle.includes(cleanSiteName)
    ? [cleanTitle, cleanSiteName].filter(Boolean).join(' | ')
    : (cleanTitle || cleanSiteName);
  const currentCanonical = canonical
    ? absoluteUrl(canonical)
    : window.location.origin + window.location.pathname.replace(/\/$/, '') || window.location.origin;
  const socialImage = absoluteUrl(image);

  document.title = pageTitle;
  setCanonical(currentCanonical);
  setNamedMeta('description', cleanDescription);
  setNamedMeta('author', 'Imaginar Diagnóstico Veterinário');
  setNamedMeta('robots', robots);
  setNamedMeta('googlebot', robots);
  setNamedMeta('twitter:card', socialImage ? 'summary_large_image' : 'summary');
  setNamedMeta('twitter:title', pageTitle);
  setNamedMeta('twitter:description', cleanDescription);
  setNamedMeta('twitter:url', currentCanonical);
  setNamedMeta('twitter:image', socialImage);
  setPropertyMeta('og:locale', 'pt_BR');
  setPropertyMeta('og:type', type);
  setPropertyMeta('og:site_name', cleanSiteName);
  setPropertyMeta('og:title', pageTitle);
  setPropertyMeta('og:description', cleanDescription);
  setPropertyMeta('og:url', currentCanonical);
  setPropertyMeta('og:image', socialImage);
  setPropertyMeta('og:image:secure_url', socialImage && socialImage.startsWith('https://') ? socialImage : '');
  setPropertyMeta('og:image:alt', socialImage ? cleanTitle : '');
  setHttpEquivMeta('Content-Language', 'pt-BR');
}

export async function loadScreens(definitions) {
  const responses = await Promise.all(definitions.map(screen => getInstitutionalContent(screen.ctiId, screen.multi)));
  return definitions.reduce((result, screen, index) => {
    result[screen.key] = responses[index];
    return result;
  }, {});
}
